import { observable, action, makeObservable } from "mobx";
import { debounce } from "throttle-debounce";
import Centrifuge from "centrifuge";

import initAxios from "../api/initAxios";
import ChatApi from "../api/ChatApi";
import callWindowFunction from "../utils/callWindowFunction";
import { sendNavigationMetric } from "../utils/sendMetric";

const updatePresenceIntervalLag = 120000;
class Chat {
  errorMessage = null;

  constructor(rootStore, chatUrl) {
    makeObservable(this, {
      chatUnreadCount: observable,
      setChatUnreadCount: action,
    });

    this.rootStore = rootStore;

    this.reloadPresenceInterval = null;
    this.isChatWsStarted = false;
    this.getChatWsTokenUrl = window.location
      ? `${window.location.protocol}//chat.${this.rootStore.baseUrl}/api/ws/`
      : `https//chat.${this.rootStore.baseUrl}/api/ws/`;
    this.websocket = null;
    this.wsConnectionCount = 0;

    this.ChatApi = new ChatApi(chatUrl);
    this.axios = initAxios({
      withCredentials: true,
    });
  }

  /** Количество непрочитанных сообщений */
  chatUnreadCount = undefined;

  /** Изменить количество непрочитанных сообщений */
  setChatUnreadCount = (value) => {
    this.chatUnreadCount = value;
  };

  startUpdatePresenceInterval = () => {
    this.updatePresence();
    this.reloadPresenceInterval = setInterval(
      this.updatePresence,
      updatePresenceIntervalLag
    );
  };

  clearUpdatePresenceInterval = () => {
    clearInterval(this.reloadPresenceInterval);
  };

  reloadChat = () => {
    this.ChatApi.getUnreadCount()
      .then(({ data }) => {
        this.setChatUnreadCount(data.unread);

        callWindowFunction("publishToEventBus", [
          {
            subject: "frontChatUnreadCount",
            destination: "exceptThisTab",
            tabId: this.rootStore.profile.tabId,
            body: data.unread,
          },
        ]);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  updatePresence = () => {
    // этот счетчик обновляет присутствие пользователя на сайте
    this.ChatApi.updatePresence().catch((error) => console.log(error));
  };

  createPopupNotificationForChatUsers = (msg) => {
    if (msg && msg.data.invite) {
      return;
    }

    this.handleChatWSMessage(msg);
  };

  handleChatSettingsChange = (msg) => {
    // update counters only if message is hidden
    if (msg.data && msg.data.options && msg.data.options.hidden) {
      this.handleChatWSMessage(msg);
    }
  };

  handleChatWSMessage = (msg) => {
    if (!msg) {
      return;
    }

    callWindowFunction("publishToEventBus", [
      {
        subject: "frontWSChatMessage",
        destination: "exceptThisTab",
        tabId: this.rootStore.profile.tabId,
        body: msg,
      },
    ]);

    this.reloadChat();
    this.createPopupNotification(msg);
  };

  handleChatWSMessageRead = (msg) => {
    if (!msg) {
      return;
    }

    callWindowFunction("publishToEventBus", [
      {
        subject: "frontWSChatMessageRead",
        destination: "exceptThisTab",
        tabId: this.rootStore.profile.tabId,
        body: msg,
      },
    ]);

    this.reloadChat();
    this.handleReadAllChannelMessages(msg);
  };

  handleReadAllChannelMessages = (msg) => {
    this.rootStore.feeds.closeAllChannelMessages(msg.data.channel);
  };

  createPopupNotification = (msg) => {
    this.rootStore.feeds.listenNtf(msg);
  };

  getChatToken = () => {
    this.axios
      .post(this.getChatWsTokenUrl)
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          this.openChatWSConnection(res.data);
        }
        return null;
      })
      .catch((error) => {
        console.log(error);
        this.errorMessage = error;
        this.isChatWsStarted = false;
        if (this.rootStore.crossDomain.isMaster) {
          this.reopenChatWsConnection();
        }
      });
  };

  reopenChatWsConnection = debounce(500, false, () => {
    if (this.wsConnectionCount === 3) {
      this.rootStore.notifications.startUpdateIntervalNotifications();
    }

    this.wsConnectionCount += 1;
    const lag =
      5000 * this.wsConnectionCount < 60000
        ? 5000 * this.wsConnectionCount
        : 60000;

    window.setTimeout(() => {
      if (!this.websocket) {
        this.getChatToken();
      }
    }, lag);
  });

  closeChatWSConnection = () => {
    this.isChatWsStarted = false;

    if (this.websocket) {
      this.websocket.disconnect();
    }
  };

  openChatWSConnection = (wsData) => {
    const data = {
      ...wsData,
      url: `wss://wss.${this.rootStore.baseUrl}/socket/`,
    };

    this.websocket = new Centrifuge(data);
    this.websocket.subscribe(`messages#${data.user}`, this.handleChatWSMessage);
    this.websocket.subscribe(
      `groups.messages#${data.user}`,
      this.createPopupNotificationForChatUsers
    );
    this.websocket.subscribe(
      `channels.read#${data.user}`,
      this.handleChatWSMessageRead
    );
    this.websocket.subscribe(
      `messages.deleted#${data.user}`,
      this.handleChatWSMessage
    );
    this.websocket.subscribe(
      `groups.invite#${data.user}`,
      this.handleChatWSMessage
    );
    this.websocket.subscribe(
      `groups.exclude#${data.user}`,
      this.handleChatWSMessage
    );
    this.websocket.subscribe(
      `channels.settings#${data.user}`,
      this.handleChatSettingsChange
    );
    this.websocket.connect();

    this.wsConnectionCount = 0;
    this.isChatWsStarted = true;
  };

  chatMasterAction = () => {
    // обновление присутствия пользователя
    this.startUpdatePresenceInterval();

    // открытие ws чата
    if (!this.isChatWsStarted) {
      this.getChatToken();
    }

    if (!this.rootStore.crossDomain.isWasLed) {
      // получение актуального счетчика чата
      this.reloadChat();
    }
  };

  finalChatMasterAction = () => {
    if (this.isChatWsStarted) {
      this.closeChatWSConnection();
    }
    this.clearUpdatePresenceInterval();
  };

  getWSInfo = () => [
    `Is chat ws working: ${this.isChatWsStarted}`,
    `Count of connection to chat ws: ${this.wsConnectionCount}`,
    `Error message: ${this.errorMessage}`,
  ];

  handleChatButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    sendNavigationMetric("CHAT_BUTTON");

    // only for chat.ati.su
    if (/chat\./i.test(location.href)) {
      // new Event("openLatestChat") doesn't work in IE :)
      const openLatestChatEvent = document.createEvent("Event");
      openLatestChatEvent.initEvent("openLatestChat", true, true);
      window.dispatchEvent(openLatestChatEvent);
    }

    window.open(
      `//chat.${this.rootStore.baseUrl}?utm_source=header&utm_campaign=new_header#latest`
    );
  };
}

export default Chat;
